import { TFunction } from 'i18next';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpOutlinedIcon from '@mui/icons-material/ArrowDropUpOutlined';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { SxProps, useTheme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { FieldValues, useFormContext, UseFormGetValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import ButtonResetIcon from '../ButtonResetIcon';
import {
  arrowStyling,
  darkBackgroundColor,
  defaultButtonStyling,
  definedFilterStyling,
  drawerSelectButtonStyling,
  dropDownSelectButtonStyling,
  selectButtonLabel,
  selectedButtonStyling,
  singleSelectButtonFontSize,
  singleSelectButtonStyling,
  getDarkModeButtonSx,
  getErrorButtonSx,
} from '../styles';
import { ButtonSize, SingleSelectGroup } from '../types';
import { customStartIconStyles } from './styles';

interface SingleSelectButtonProps {
  isMobile: boolean;
  handleClick: (event: React.MouseEvent<HTMLInputElement | HTMLButtonElement>) => void;
  buttonText: string;
  selectedLabel: string;
  buttonSize: ButtonSize;
  anchorEl: HTMLInputElement | HTMLButtonElement | null;
  options: SingleSelectGroup[];
  controllerName: string;
  handleReset: () => void;
  predefined: boolean;
  useAsDateComparison: boolean;
  resetValue: boolean;
  useAsJurisdiction?: boolean;
  labelText?: string;
  isDrawerSelect?: boolean;
  preDefinedFilterSelection?: boolean;
  isDropDownSelect?: boolean;
  hasError?: boolean;
  disabled?: boolean;
  required?: boolean;
  customStartIcon?: React.ReactNode;
}

export function handleText(
  t: TFunction,
  useAsDateComparison: boolean,
  getValues: UseFormGetValues<FieldValues>,
  controllerName: string,
  selectedLabel: string,
  isDrawerSelect: boolean | undefined,
  anchorEl: HTMLInputElement | HTMLButtonElement | null,
  inputText: string
) {
  const watching = getValues(controllerName);
  if (useAsDateComparison && watching.startDate) {
    const startDayjs = dayjs(watching.startDate);
    const endDayjs = dayjs(watching.endDate);

    const formattedStartDate = startDayjs.format('L');
    const formattedEndDate = endDayjs.format('L');

    if (startDayjs.isSame(endDayjs, 'day') || endDayjs === undefined) {
      return `${t('sales.dateRanges.comparedTo')} ${formattedStartDate}`;
    }
    return `${t('sales.dateRanges.comparedTo')} ${formattedStartDate} - ${formattedEndDate}`;
  }
  if (selectedLabel && selectedLabel !== '') return selectedLabel;
  if (isDrawerSelect && anchorEl) return '';
  return inputText;
}
function SingleSelectButton(props: SingleSelectButtonProps) {
  const {
    isMobile,
    handleClick,
    buttonText,
    selectedLabel,
    buttonSize,
    anchorEl,
    options,
    controllerName,
    handleReset,
    predefined,
    useAsDateComparison,
    resetValue,
    useAsJurisdiction,
    labelText,
    isDrawerSelect,
    preDefinedFilterSelection,
    isDropDownSelect,
    hasError,
    disabled,
    required,
    customStartIcon,
  } = props;

  const theme = useTheme();
  const { t } = useTranslation();
  const isDarkMode = theme.palette.mode === 'dark';

  const form = useFormContext();
  const {
    getValues,
    formState: { dirtyFields: dirtiness },
  } = form;
  const watching = getValues(controllerName);

  const dirtyValue = Object.keys(dirtiness).includes(controllerName);

  const buttonTextSx: SxProps = singleSelectButtonStyling(isDrawerSelect);
  const fontSize = singleSelectButtonFontSize(isDrawerSelect);
  const buttonDisplayText = handleText(
    t,
    useAsDateComparison,
    getValues,
    controllerName,
    selectedLabel,
    isDrawerSelect,
    anchorEl,
    buttonText
  );

  const handleButtonSxPredefined = () => {
    if (
      Boolean(anchorEl) ||
      (options.findIndex((label) => label.label === selectedLabel) !== 0 && selectedLabel !== '')
    ) {
      if (!isDarkMode) {
        return {
          backgroundColor: `${theme.palette.primary.main}${theme.palette.action.hoverOpacity
            .toString(16)
            .substring(2, 4)}`,
        };
      }
      return {
        backgroundColor: darkBackgroundColor,
        color: theme.palette.text.primary,
      };
    }
    return definedFilterStyling(theme);
  };

  const isDarkModeActive = (): boolean =>
    Boolean(
      (useAsDateComparison && watching.startDate !== null && watching.endDate !== null) ||
        (dirtyValue && !useAsDateComparison) ||
        useAsJurisdiction
    );

  const isSelectedState = () =>
    (useAsDateComparison && watching.startDate !== null && watching.endDate !== null) ||
    (dirtyValue && !useAsDateComparison) ||
    useAsJurisdiction ||
    preDefinedFilterSelection;

  const handleButtonSx = () => {
    // Error state takes priority over all other styling
    if (hasError) {
      return getErrorButtonSx(anchorEl, theme, isDrawerSelect, isDropDownSelect);
    }
    if (isDropDownSelect) return dropDownSelectButtonStyling(anchorEl, theme);

    if (isDarkMode) {
      return getDarkModeButtonSx(isDarkModeActive(), theme);
    }

    if (isDrawerSelect) return drawerSelectButtonStyling(anchorEl, theme);

    if (isSelectedState()) {
      return selectedButtonStyling(anchorEl, theme);
    }

    return defaultButtonStyling(anchorEl, theme);
  };

  const renderButtonText = () => {
    if (isDropDownSelect) {
      return buttonDisplayText;
    }

    return (
      <Typography sx={buttonTextSx} fontSize={fontSize}>
        {handleText(
          t,
          useAsDateComparison,
          getValues,
          controllerName,
          selectedLabel,
          isDrawerSelect,
          anchorEl,
          buttonText
        )}
      </Typography>
    );
  };

  const getButtonText = () => {
    if (isDropDownSelect && buttonText) {
      return buttonDisplayText;
    }

    return (
      <Typography sx={buttonTextSx} fontSize={fontSize}>
        {buttonDisplayText}
      </Typography>
    );
  };

  const buttonDataTestId = `${controllerName}-single-select-button`;
  const button = (
    <Button
      role='button'
      aria-haspopup='true'
      data-testid={buttonDataTestId}
      sx={{
        ...handleButtonSx(),
        ...(customStartIcon && customStartIconStyles()),
      }}
      variant={isDrawerSelect ? 'outlined' : undefined}
      onClick={handleClick}
      size={buttonSize}
      aria-label={useAsDateComparison ? `${buttonDisplayText}` : `${buttonText}, ${selectedLabel} selected`}
      disabled={disabled}
      data-error={hasError ? 'true' : 'false'}
      startIcon={customStartIcon}
    >
      {!isDrawerSelect && ((resetValue && useAsDateComparison) || (dirtyValue && !useAsDateComparison)) ? (
        <>
          {renderButtonText()}
          {resetValue && dirtyValue ? (
            <ButtonResetIcon handleReset={handleReset} isDropDownSelect={isDropDownSelect} />
          ) : (
            <ArrowDropDownIcon sx={arrowStyling(theme, false)} fontSize={fontSize} />
          )}
        </>
      ) : (
        <>
          {getButtonText()}
          {anchorEl ? (
            <ArrowDropUpOutlinedIcon sx={arrowStyling(theme, false)} fontSize={fontSize} />
          ) : (
            <ArrowDropDownIcon sx={arrowStyling(theme, false)} fontSize={fontSize} />
          )}
        </>
      )}
    </Button>
  );
  const getLabelColor = () => {
    if (hasError) return 'error.main';
    if (anchorEl) return 'primary.main';
    return 'text.secondary';
  };

  const buttonElement = labelText ? (
    <Box sx={selectButtonLabel}>
      {labelText && (
        <Typography variant='caption' color={getLabelColor()}>
          {labelText}
          {required ? ' *' : ''}
        </Typography>
      )}
      {button}
    </Box>
  ) : (
    button
  );

  const predefinedButton = !isMobile ? (
    <Button
      onClick={handleClick}
      role='button'
      aria-haspopup='true'
      data-testid={buttonDataTestId}
      size='small'
      variant='outlined'
      aria-label={`${buttonText}, ${selectedLabel} selected`}
      startIcon={customStartIcon || <SwapVertIcon />}
      sx={handleButtonSxPredefined}
      data-error={hasError ? 'true' : 'false'}
    >
      <Typography
        sx={{
          fontWeight: 500,
          lineHeight: '22px',
        }}
        fontSize='inherit'
      >
        {buttonText}
      </Typography>
    </Button>
  ) : (
    <Button
      data-testid={buttonDataTestId}
      onClick={handleClick}
      sx={handleButtonSxPredefined}
      variant='outlined'
      aria-label={`${buttonText}, ${selectedLabel} selected`}
      size={buttonSize}
      data-error={hasError ? 'true' : 'false'}
    >
      {customStartIcon || <SwapVertIcon />}
    </Button>
  );

  return predefined ? predefinedButton : buttonElement;
}

SingleSelectButton.defaultProps = {
  useAsJurisdiction: false,
  labelText: undefined,
  isDrawerSelect: false,
  isDropDownSelect: false,
  disabled: false,
};

export default SingleSelectButton;
